import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Checkbox } from '../../components/ui/checkbox';
import { Textarea } from '../../components/ui/textarea';
import { Progress } from '../../components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { useAdminAuth } from '../../context/AdminAuthContext';
import {
  Loader2, ArrowLeft, CheckCircle2, Clock, AlertCircle, XCircle, Cloud,
  RefreshCw, Key, Trash2, AlertTriangle, HardDrive, Users, Zap, Shield,
  ExternalLink, Copy, User, Calendar, Server, Globe, Database, CreditCard
} from 'lucide-react';
import { toast } from 'sonner';

export const AdminCloudDetail = () => {
  const { cloudId } = useParams();
  const navigate = useNavigate();
  const { token, hasPermission } = useAdminAuth();
  const [instance, setInstance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scapiStatus, setScapiStatus] = useState(null);
  const [loadingScapi, setLoadingScapi] = useState(false);
  const [scapiModalOpen, setScapiModalOpen] = useState(false);

  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteFromScapi, setDeleteFromScapi] = useState(false);
  const [deleteReason, setDeleteReason] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [confirmDeleteText, setConfirmDeleteText] = useState('');

  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  const canEdit = hasPermission('clouds:edit');

  const fetchInstance = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${backendUrl}/api/admin/clouds/${cloudId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setInstance(data);
      } else {
        toast.error('Cloud nicht gefunden');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInstance();
  }, [cloudId, token]);

  const fetchScapiStatus = async () => {
    if (!instance?.scapi_instance_id) return;
    
    setLoadingScapi(true);
    try {
      const response = await fetch(`${backendUrl}/api/admin/clouds/${cloudId}/scapi-status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setScapiStatus(data);
        setScapiModalOpen(true);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'SCAPI Status nicht verfügbar');
      }
    } catch (error) {
      toast.error('Fehler beim Abrufen');
    } finally {
      setLoadingScapi(false);
    }
  };

  const handleRestartProvisioning = async () => {
    if (!confirm('Provisioning wirklich neu starten?')) return;
    
    try {
      const response = await fetch(`${backendUrl}/api/admin/clouds/${cloudId}/restart-provisioning`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success('Provisioning neu gestartet');
        fetchInstance();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Fehler');
      }
    } catch (error) {
      toast.error('Fehler beim Neustart');
    }
  };

  const handleResetPassword = async () => {
    if (!confirm('Admin-Passwort wirklich zurücksetzen?')) return;
    
    try {
      const response = await fetch(`${backendUrl}/api/admin/clouds/${cloudId}/reset-password`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(`Neues Passwort: ${data.admin_password}`);
        fetchInstance();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Fehler');
      }
    } catch (error) {
      toast.error('Fehler beim Zurücksetzen');
    }
  };

  const handleDelete = async () => {
    if (confirmDeleteText !== 'LÖSCHEN') {
      toast.error('Bitte "LÖSCHEN" eingeben zur Bestätigung');
      return;
    }

    setDeleting(true);
    try {
      const response = await fetch(`${backendUrl}/api/admin/clouds/${cloudId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          delete_from_scapi: deleteFromScapi,
          reason: deleteReason || undefined
        })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success('Cloud erfolgreich gelöscht');
        if (data.errors && data.errors.length > 0) {
          data.errors.forEach(err => toast.warning(err));
        }
        navigate('/admin/clouds');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Fehler beim Löschen');
      }
    } catch (error) {
      toast.error('Fehler beim Löschen');
    } finally {
      setDeleting(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Kopiert!');
  };

  const getStatusBadge = (status) => {
    const configs = {
      'ready': { class: 'bg-green-500/20 text-green-400', icon: CheckCircle2, label: 'Aktiv' },
      'pending': { class: 'bg-yellow-500/20 text-yellow-400', icon: Clock, label: 'Ausstehend' },
      'deploying': { class: 'bg-blue-500/20 text-blue-400', icon: Cloud, label: 'Deploying' },
      'configuring': { class: 'bg-purple-500/20 text-purple-400', icon: RefreshCw, label: 'Konfiguriert' },
      'failed': { class: 'bg-red-500/20 text-red-400', icon: AlertCircle, label: 'Fehler' },
    };
    return configs[status] || configs['pending'];
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleString('de-DE');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (!instance) {
    return (
      <div className="text-center py-20">
        <p className="text-slate-500">Cloud nicht gefunden</p>
        <Button asChild variant="link" className="mt-4 text-blue-400">
          <Link to="/admin/clouds">Zurück zur Übersicht</Link>
        </Button>
      </div>
    );
  }

  const statusConfig = getStatusBadge(instance.status);
  const StatusIcon = statusConfig.icon;

  return (
    <div className="space-y-6" data-testid="admin-cloud-detail">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <Button asChild variant="ghost" size="sm" className="text-slate-400 hover:text-white mb-2">
            <Link to="/admin/clouds">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Zurück zu Clouds
            </Link>
          </Button>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-white">
              {instance.plan?.custom_domain || 'Cloud Details'}
            </h1>
            <Badge className={statusConfig.class}>
              <StatusIcon className="h-3 w-3 mr-1" />
              {statusConfig.label}
            </Badge>
          </div>
          {instance.url && (
            <a href={instance.url} target="_blank" rel="noopener noreferrer" 
               className="text-blue-400 hover:underline flex items-center gap-1 mt-1">
              {instance.url}
              <ExternalLink className="h-4 w-4" />
            </a>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={fetchInstance} className="border-slate-600 text-slate-300">
            <RefreshCw className="h-4 w-4 mr-2" />
            Aktualisieren
          </Button>
          {canEdit && (
            <Button 
              variant="destructive" 
              size="sm" 
              onClick={() => setDeleteDialogOpen(true)}
              className="bg-red-600 hover:bg-red-700"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Löschen
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Instance Info */}
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-white">
                <Server className="h-5 w-5 text-blue-400" />
                Instanz-Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-400">Instanz-ID</p>
                  <p className="font-mono text-sm text-white flex items-center gap-2">
                    {instance.id}
                    <button onClick={() => copyToClipboard(instance.id)} className="text-slate-500 hover:text-white">
                      <Copy className="h-3 w-3" />
                    </button>
                  </p>
                </div>
                <div>
                  <p className="text-sm text-slate-400">Status</p>
                  <Badge className={statusConfig.class}>
                    <StatusIcon className="h-3 w-3 mr-1" />
                    {statusConfig.label}
                  </Badge>
                  {instance.progress > 0 && instance.progress < 100 && (
                    <div className="mt-2">
                      <Progress value={instance.progress} className="h-2" />
                      <span className="text-xs text-slate-400">{instance.progress}%</span>
                    </div>
                  )}
                </div>
                <div>
                  <p className="text-sm text-slate-400">Erstellt</p>
                  <p className="text-white">{formatDate(instance.created_at)}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-400">URL</p>
                  {instance.url ? (
                    <a href={instance.url} target="_blank" rel="noopener noreferrer" 
                       className="text-blue-400 hover:underline flex items-center gap-1">
                      {instance.url}
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  ) : (
                    <span className="text-slate-500">-</span>
                  )}
                </div>
              </div>

              {/* Actions */}
              {canEdit && (
                <div className="flex flex-wrap gap-2 pt-4 border-t border-slate-700">
                  {instance.status === 'failed' && (
                    <Button variant="outline" size="sm" onClick={handleRestartProvisioning} className="border-slate-600 text-slate-300">
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Provisioning neu starten
                    </Button>
                  )}
                  {instance.status === 'ready' && (
                    <Button variant="outline" size="sm" onClick={handleResetPassword} className="border-slate-600 text-slate-300">
                      <Key className="h-4 w-4 mr-2" />
                      Passwort zurücksetzen
                    </Button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* SCAPI Info */}
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-white">
                <Database className="h-5 w-5 text-blue-400" />
                SCAPI Instance
              </CardTitle>
            </CardHeader>
            <CardContent>
              {instance.scapi_instance_id ? (
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-slate-400">SCAPI Instance ID</p>
                    <p className="font-mono text-white flex items-center gap-2">
                      {instance.scapi_instance_id}
                      <button onClick={() => copyToClipboard(instance.scapi_instance_id)} className="text-slate-500 hover:text-white">
                        <Copy className="h-3 w-3" />
                      </button>
                    </p>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={fetchScapiStatus}
                    disabled={loadingScapi}
                    className="border-slate-600 text-slate-300"
                  >
                    {loadingScapi ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Server className="h-4 w-4 mr-2" />
                    )}
                    Live Status abrufen
                  </Button>
                </div>
              ) : (
                <p className="text-slate-500">Keine SCAPI Instance ID vorhanden</p>
              )}
            </CardContent>
          </Card>

          {/* Plan Info */}
          {instance.plan && (
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-white">
                  <Cloud className="h-5 w-5 text-blue-400" />
                  Plan-Konfiguration
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="flex items-center gap-2">
                    <HardDrive className="h-5 w-5 text-slate-500" />
                    <div>
                      <p className="text-sm text-slate-400">Speicher</p>
                      <p className="font-semibold text-white">{instance.plan.storage_gb} GB</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Users className="h-5 w-5 text-slate-500" />
                    <div>
                      <p className="text-sm text-slate-400">Office-Nutzer</p>
                      <p className="font-semibold text-white">{instance.plan.collabora_users || 0}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Zap className={`h-5 w-5 ${instance.plan.hp_backend ? 'text-amber-500' : 'text-slate-600'}`} />
                    <div>
                      <p className="text-sm text-slate-400">Turbo</p>
                      <p className="font-semibold text-white">{instance.plan.hp_backend ? 'Ja' : 'Nein'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Shield className={`h-5 w-5 ${instance.plan.antivirus ? 'text-green-500' : 'text-slate-600'}`} />
                    <div>
                      <p className="text-sm text-slate-400">Antivirus</p>
                      <p className="font-semibold text-white">{instance.plan.antivirus ? 'Ja' : 'Nein'}</p>
                    </div>
                  </div>
                </div>
                {instance.plan.custom_domain && (
                  <div className="mt-4 pt-4 border-t border-slate-700">
                    <div className="flex items-center gap-2">
                      <Globe className="h-5 w-5 text-slate-500" />
                      <div>
                        <p className="text-sm text-slate-400">Domain</p>
                        <p className="font-semibold text-white">{instance.plan.custom_domain}</p>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Customer Info */}
          {instance.customer && (
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-white">
                  <User className="h-5 w-5 text-blue-400" />
                  Kunde
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <p className="text-sm text-slate-400">Name</p>
                  <p className="text-white">
                    {instance.customer.first_name} {instance.customer.last_name}
                  </p>
                </div>
                {instance.customer.company && (
                  <div>
                    <p className="text-sm text-slate-400">Firma</p>
                    <p className="text-white">{instance.customer.company}</p>
                  </div>
                )}
                <div>
                  <p className="text-sm text-slate-400">E-Mail</p>
                  <p className="text-white">{instance.customer.email}</p>
                </div>
                <Button asChild variant="outline" size="sm" className="w-full mt-2 border-slate-600 text-slate-300">
                  <Link to={`/admin/customers/${instance.customer.id}`}>
                    Kunde anzeigen
                  </Link>
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Subscription Info */}
          {instance.subscription && (
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-white">
                  <CreditCard className="h-5 w-5 text-blue-400" />
                  Abonnement
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <p className="text-sm text-slate-400">Monatlicher Preis</p>
                  <p className="text-white font-semibold">
                    €{instance.subscription.monthly_price_brutto?.toFixed(2) || '0.00'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-slate-400">Status</p>
                  <Badge className={instance.subscription.status === 'active' ? 'bg-green-500/20 text-green-400' : 'bg-slate-500/20 text-slate-400'}>
                    {instance.subscription.status === 'active' ? 'Aktiv' : instance.subscription.status}
                  </Badge>
                </div>
                <div>
                  <p className="text-sm text-slate-400">Erstellt</p>
                  <p className="text-white text-sm">{formatDate(instance.subscription.created_at)}</p>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* SCAPI Status Modal */}
      <Dialog open={scapiModalOpen} onOpenChange={setScapiModalOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-hidden bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-white">
              <Server className="h-5 w-5" />
              SCAPI Live Status
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {instance.scapi_instance_id}
            </DialogDescription>
          </DialogHeader>
          <div className="overflow-auto max-h-[60vh]">
            {scapiStatus ? (
              <pre className="bg-slate-900 p-4 rounded-lg text-xs overflow-auto font-mono text-slate-300">
                {JSON.stringify(scapiStatus.raw_json || scapiStatus, null, 2)}
              </pre>
            ) : (
              <p className="text-slate-500 text-center py-8">Keine Daten</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setScapiModalOpen(false)} className="border-slate-600 text-slate-300">
              Schließen
            </Button>
            <Button onClick={fetchScapiStatus} disabled={loadingScapi} className="bg-blue-600 hover:bg-blue-700">
              {loadingScapi ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
              Neu laden
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="max-w-lg bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-400">
              <AlertTriangle className="h-5 w-5" />
              Cloud löschen
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Diese Aktion kann nicht rückgängig gemacht werden.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="bg-slate-700/50 rounded-lg p-4 space-y-2">
              <p className="font-medium text-white">
                {instance.plan?.custom_domain || 'Unbenannte Cloud'}
              </p>
              {instance.customer && (
                <p className="text-sm text-slate-400">
                  Kunde: {instance.customer.email}
                </p>
              )}
              {instance.scapi_instance_id && (
                <p className="text-xs font-mono text-slate-500">
                  SCAPI: {instance.scapi_instance_id}
                </p>
              )}
            </div>

            <div className="bg-red-900/20 border border-red-700/50 rounded-lg p-4">
              <p className="text-sm font-medium text-red-400 mb-2">Folgendes wird gelöscht:</p>
              <ul className="text-sm text-slate-400 space-y-1 list-disc list-inside">
                <li>Instanz-Datensatz</li>
                <li>Subscription/Vertrag</li>
                <li>Plan-Konfiguration</li>
                <li>Bestellungen werden als storniert markiert</li>
                <li>Rechnungen werden als storniert markiert</li>
              </ul>
            </div>

            {instance.scapi_instance_id && (
              <div className="flex items-start gap-3 p-4 bg-amber-900/20 border border-amber-700/50 rounded-lg">
                <Checkbox
                  id="delete-scapi-detail"
                  checked={deleteFromScapi}
                  onCheckedChange={setDeleteFromScapi}
                  className="mt-0.5 border-slate-500"
                />
                <div className="flex-1">
                  <label htmlFor="delete-scapi-detail" className="text-sm font-medium text-white cursor-pointer">
                    Auch bei SCAPI löschen
                  </label>
                  <p className="text-xs text-slate-400 mt-1">
                    Löscht die Nextcloud-Instanz auch beim Hosting-Provider. 
                    <span className="text-amber-400 font-medium"> Alle Daten des Kunden gehen verloren!</span>
                  </p>
                </div>
              </div>
            )}

            <div>
              <label className="text-sm font-medium text-white mb-2 block">
                Grund für Löschung (optional)
              </label>
              <Textarea
                value={deleteReason}
                onChange={(e) => setDeleteReason(e.target.value)}
                placeholder="z.B. Kunde hat gekündigt, Test-Instanz, etc."
                className="min-h-20 bg-slate-700 border-slate-600 text-white placeholder:text-slate-500"
              />
            </div>

            <div>
              <label className="text-sm font-medium text-white mb-2 block">
                Zur Bestätigung "LÖSCHEN" eingeben:
              </label>
              <Input
                value={confirmDeleteText}
                onChange={(e) => setConfirmDeleteText(e.target.value.toUpperCase())}
                placeholder="LÖSCHEN"
                className="font-mono bg-slate-700 border-slate-600 text-white"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} disabled={deleting} className="border-slate-600 text-slate-300">
              Abbrechen
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleting || confirmDeleteText !== 'LÖSCHEN'}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Lösche...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Endgültig löschen
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
